import * as React from 'react';
import { CheckCircle2Icon, CircleAlertIcon, MailIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  engagementMeta,
  interestOptions,
  type EngagementInterest
} from '@/content/engagement';
import { TDC_CONTACT_EMAIL, TDC_CONTACT_MAILTO } from '@/lib/external-links';
import { cn } from '@/lib/utils';

type FormStatus = 'idle' | 'error' | 'success' | 'submit-error';

type InterestFormProps = {
  idPrefix?: string;
  defaultInterest?: EngagementInterest;
  className?: string;
  heading?: string;
  description?: string;
};

type FieldErrors = {
  name?: string;
  email?: string;
  interest?: string;
  message?: string;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function buildMailto(values: {
  name: string;
  email: string;
  organisation: string;
  interest: EngagementInterest;
  message: string;
}): string {
  const interestLabel =
    interestOptions.find((option) => option.value === values.interest)?.label ?? values.interest;

  const subject = encodeURIComponent(
    `[NextOnVera] ${interestLabel} — ${values.name}`
  );
  const body = encodeURIComponent(
    [
      `Name: ${values.name}`,
      `Email: ${values.email}`,
      values.organisation ? `Organisation: ${values.organisation}` : null,
      `Interest: ${interestLabel}`,
      '',
      values.message
    ]
      .filter(Boolean)
      .join('\n')
  );

  return `mailto:${TDC_CONTACT_EMAIL}?subject=${subject}&body=${body}`;
}

export function InterestForm({
  idPrefix = 'interest',
  defaultInterest = 'general',
  className,
  heading = 'Register interest',
  description = `Validated messages open a prepared email to ${engagementMeta.contactEmail}. A full managed mailbox can be wired later without changing this form.`
}: InterestFormProps) {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [organisation, setOrganisation] = React.useState('');
  const [interest, setInterest] = React.useState<EngagementInterest>(defaultInterest);
  const [message, setMessage] = React.useState('');
  const [status, setStatus] = React.useState<FormStatus>('idle');
  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const successRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setInterest(defaultInterest);
  }, [defaultInterest]);

  React.useEffect(() => {
    if (status === 'success' && successRef.current) {
      successRef.current.focus();
    }
  }, [status]);

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = 'Please enter your name.';
    if (!email.trim()) next.email = 'Please enter your email.';
    else if (!EMAIL_PATTERN.test(email.trim())) next.email = 'Enter a valid email address.';
    if (!interest) next.interest = 'Choose how you would like to get involved.';
    if (!message.trim() || message.trim().length < 12) {
      next.message = 'Add a short note (at least a sentence) so we can respond helpfully.';
    }
    return next;
  }

  function resetForm() {
    setName('');
    setEmail('');
    setOrganisation('');
    setInterest(defaultInterest);
    setMessage('');
    setErrors({});
    setFormError(null);
    setStatus('idle');
  }

  function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setStatus('error');
      setFormError('Please fix the highlighted fields and try again.');
      return;
    }

    try {
      const mailto = buildMailto({
        name: name.trim(),
        email: email.trim(),
        organisation: organisation.trim(),
        interest,
        message: message.trim()
      });
      window.location.href = mailto;
      setStatus('success');
      setFormError(null);
    } catch {
      setStatus('submit-error');
      setFormError(
        `We couldn’t open your email client. Email ${TDC_CONTACT_EMAIL} directly and we’ll take it from there.`
      );
    }
  }

  if (status === 'success') {
    return (
      <div
        ref={successRef}
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className={cn(
          'border-primary/30 bg-primary/10 rounded-2xl border px-6 py-8 shadow-sm outline-none sm:px-8',
          className
        )}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2Icon className="text-primary mt-0.5 size-6 shrink-0" aria-hidden />
          <div className="space-y-3">
            <h3 className="font-display text-foreground text-xl font-semibold tracking-tight">
              Thank you — your message is ready
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
              We opened a prepared email to{' '}
              <a
                href={TDC_CONTACT_MAILTO}
                className="text-primary underline-offset-4 hover:underline"
              >
                {TDC_CONTACT_EMAIL}
              </a>
              . Send it from your mail app to complete the request. If nothing appeared, use the
              address above or try again.
            </p>
            <div className="flex flex-col gap-2 pt-2 sm:flex-row">
              <Button type="button" variant="cta" onClick={resetForm}>
                Send another message
              </Button>
              <Button asChild type="button" variant="outline">
                <a href={TDC_CONTACT_MAILTO}>
                  <MailIcon aria-hidden />
                  Email {TDC_CONTACT_EMAIL}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const nameId = `${idPrefix}-name`;
  const emailId = `${idPrefix}-email`;
  const orgId = `${idPrefix}-organisation`;
  const interestId = `${idPrefix}-interest`;
  const messageId = `${idPrefix}-message`;
  const errorId = `${idPrefix}-form-error`;

  return (
    <div
      className={cn(
        'border-border/70 bg-card/55 rounded-2xl border px-6 py-8 shadow-sm backdrop-blur-sm sm:px-8',
        className
      )}
    >
      <div className="mb-6 space-y-2">
        <h3 className="font-display text-foreground text-xl font-semibold tracking-tight sm:text-2xl">
          {heading}
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">{description}</p>
      </div>

      {(status === 'error' || status === 'submit-error') && formError ? (
        <div
          id={errorId}
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mb-6 flex items-start gap-2 rounded-lg border px-3 py-3 text-sm"
        >
          <CircleAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
          <p>{formError}</p>
        </div>
      ) : null}

      <form
        noValidate
        onSubmit={onSubmit}
        className="grid gap-5"
        aria-describedby={formError ? errorId : undefined}
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor={nameId}>
              Full name <span className="text-cta">*</span>
            </Label>
            <Input
              id={nameId}
              name="name"
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? `${nameId}-error` : undefined}
            />
            {errors.name ? (
              <p id={`${nameId}-error`} className="text-destructive text-xs">
                {errors.name}
              </p>
            ) : null}
          </div>

          <div className="grid gap-2">
            <Label htmlFor={emailId}>
              Email <span className="text-cta">*</span>
            </Label>
            <Input
              id={emailId}
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? `${emailId}-error` : undefined}
            />
            {errors.email ? (
              <p id={`${emailId}-error`} className="text-destructive text-xs">
                {errors.email}
              </p>
            ) : null}
          </div>
        </div>

        <div className="grid gap-2">
          <Label htmlFor={orgId}>Organisation (optional)</Label>
          <Input
            id={orgId}
            name="organisation"
            autoComplete="organization"
            value={organisation}
            onChange={(event) => setOrganisation(event.target.value)}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor={interestId}>
            How can we help? <span className="text-cta">*</span>
          </Label>
          <Select
            value={interest}
            onValueChange={(value) => setInterest(value as EngagementInterest)}
          >
            <SelectTrigger
              id={interestId}
              className="w-full min-w-0"
              aria-invalid={Boolean(errors.interest)}
              aria-describedby={errors.interest ? `${interestId}-error` : undefined}
            >
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {interestOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.interest ? (
            <p id={`${interestId}-error`} className="text-destructive text-xs">
              {errors.interest}
            </p>
          ) : null}
        </div>

        <div className="grid gap-2">
          <Label htmlFor={messageId}>
            Message <span className="text-cta">*</span>
          </Label>
          <Textarea
            id={messageId}
            name="message"
            rows={5}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            aria-invalid={Boolean(errors.message)}
            aria-describedby={errors.message ? `${messageId}-error` : undefined}
            placeholder="A few sentences about how you’d like to help, or what you need from the team."
          />
          {errors.message ? (
            <p id={`${messageId}-error`} className="text-destructive text-xs">
              {errors.message}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-foreground text-xs leading-relaxed">
            Required fields marked *. By sending, you agree we may reply about NextOnVera
            and Tour de Cure programmes.
          </p>
          <Button type="submit" variant="cta" size="lg" className="sm:min-w-44">
            <MailIcon aria-hidden />
            Send message
          </Button>
        </div>

        {status === 'submit-error' ? (
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={TDC_CONTACT_MAILTO}>Email {TDC_CONTACT_EMAIL}</a>
            </Button>
          </div>
        ) : null}
      </form>
    </div>
  );
}
